import React, { useState } from 'react';
import { useEmailLogs, useResendEmail, type EmailLog } from '@/hooks/useEmailLogs';
import { useEmailTemplates } from '@/hooks/useEmailTemplates';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RefreshCw, Send, CheckCircle, XCircle, Clock, User } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

const EmailLogsTable = () => {
  const [templateFilter, setTemplateFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  const { data: logs, isLoading, refetch } = useEmailLogs({
    templateKey: templateFilter !== 'all' ? templateFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    limit: 100,
  });
  
  const { templates } = useEmailTemplates();
  const resendEmail = useResendEmail();

  const handleResend = async (log: EmailLog) => {
    try {
      await resendEmail.mutateAsync(log);
      toast.success('Email reenviado com sucesso');
    } catch (error) {
      toast.error('Erro ao reenviar email');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle className="w-3 h-3 mr-1" />Enviado</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="w-3 h-3 mr-1" />Falhou</Badge>;
      default:
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <Select value={templateFilter} onValueChange={setTemplateFilter}>
          <SelectTrigger className="w-48 bg-white/5 border-white/10 text-white">
            <SelectValue placeholder="Filtrar por template" />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-white/10">
            <SelectItem value="all" className="text-white">Todos os templates</SelectItem>
            {templates?.map((t) => (
              <SelectItem key={t.template_key} value={t.template_key} className="text-white">
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-white/10">
            <SelectItem value="all" className="text-white">Todos</SelectItem>
            <SelectItem value="sent" className="text-white">Enviados</SelectItem>
            <SelectItem value="pending" className="text-white">Pendentes</SelectItem>
            <SelectItem value="failed" className="text-white">Falharam</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          className="bg-white/5 border-white/10 text-white hover:bg-white/10"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-white/60">Carregando...</div>
        ) : logs?.length === 0 ? (
          <div className="p-8 text-center text-white/60">Nenhum email encontrado</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-white/60 text-sm font-medium">Data</th>
                  <th className="text-left p-4 text-white/60 text-sm font-medium">Destinatário</th>
                  <th className="text-left p-4 text-white/60 text-sm font-medium">Template</th>
                  <th className="text-left p-4 text-white/60 text-sm font-medium">Assunto</th>
                  <th className="text-left p-4 text-white/60 text-sm font-medium">Status</th>
                  <th className="text-left p-4 text-white/60 text-sm font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {logs?.map((log) => (
                  <tr key={log.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-4 text-white/80 text-sm">
                      {format(new Date(log.created_at), "dd/MM/yy HH:mm", { locale: ptBR })}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {log.username && (
                          <span className="text-white/60 text-xs flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {log.username}
                          </span>
                        )}
                        <span className="text-white text-sm">{log.to_email}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline" className="bg-luxury-pinkBright/20 text-luxury-pinkBright border-luxury-pinkBright/30">
                        {log.template_key}
                      </Badge>
                    </td>
                    <td className="p-4 text-white/80 text-sm max-w-xs truncate">
                      {log.subject || '-'}
                    </td>
                    <td className="p-4">
                      {getStatusBadge(log.status)}
                      {log.error_message && (
                        <p className="text-xs text-red-400 mt-1 max-w-xs truncate" title={log.error_message}>
                          {log.error_message}
                        </p>
                      )}
                    </td>
                    <td className="p-4">
                      {log.status === 'failed' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResend(log)}
                          disabled={resendEmail.isPending}
                          className="text-luxury-pinkBright hover:text-luxury-pinkBright/80"
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Reenviar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailLogsTable;
